<template>
  <div v-if="open" class="report-modal-wrapper" @click.self="$emit('close')">
    <div class="report-modal-inner" role="dialog" aria-modal="true">
      <div class="report-modal-header">
        <div class="report-modal-titles">
          <h2 class="report-modal-title">{{ report?.displayName }}</h2>
          <p v-if="report?.description" class="report-modal-description">{{ report?.description }}</p>
        </div>
        <div class="report-modal-actions">
          <button
            type="button"
            class="report-modal-popout"
            title="Open in a new tab"
            @click="popOut"
          >
            Pop out &#8599;
          </button>
          <button
            type="button"
            class="report-modal-close"
            title="Close"
            @click="$emit('close')"
          >
            &#10005;
          </button>
        </div>
      </div>

      <div class="report-modal-body">
        <Loader v-if="isLoading" />
        <p v-if="error" class="report-modal-error">{{ error }}</p>
        <div ref="container" class="report-modal-embed"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from "vue";
import Loader from "@/components/Loader.vue";
import { usePowerBiEmbed } from "@/composables/reports/usePowerBiEmbed";
import type { ReportEmbedToken, ReportItem } from "@/types/reports";

const props = defineProps<{
  report: ReportItem | null;
  token: ReportEmbedToken | null;
  open: boolean;
}>();

defineEmits<{ (e: "close"): void }>();

const container = ref<HTMLDivElement | null>(null);
const { isLoading, error, embed, reset } = usePowerBiEmbed();

function popOut(): void {
  if (props.report) {
    window.open(`/reports/view/${props.report.id}`, "_blank");
  }
}

// Watch both the open flag and the report id so the modal re-embeds when the
// selected report changes (not only when it is first opened). The token is
// already fetched by the parent before the modal opens.
watch(
  () => [props.open, props.report?.id] as const,
  async ([isOpen]) => {
    if (isOpen && props.token) {
      await nextTick();
      if (container.value) {
        embed(container.value, props.token);
      }
    } else {
      reset();
    }
  }
);

onBeforeUnmount(() => {
  reset();
});
</script>

<style scoped>
.report-modal-wrapper {
  position: fixed;
  inset: 0;
  background: hwb(0deg 0% 100% / 20%);
  z-index: 99999999;
}

.report-modal-inner {
  position: absolute;
  inset: 2%;
  background: white;
  border-radius: 4px;
  box-shadow: 0 0 10px #c7c7c7;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.report-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #ddd;
}

.report-modal-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.report-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.report-modal-description {
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  color: #666;
}

.report-modal-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.report-modal-popout {
  padding: 8px 14px;
  background: #000;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.report-modal-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #555;
  line-height: 1;
}

.report-modal-body {
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.report-modal-embed {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.report-modal-error {
  padding: 16px 20px;
  color: #b00020;
  font-size: 14px;
}
</style>
